import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';

/**
 * Golden-standard close button for all modals/dialogs across the app.
 *
 * Design: transparent by default, subtle gray circle on hover.
 * Theme-aware: uses theme colors when available.
 *
 * Sizes:
 *   'sm'  → 32px button, 16px icon  (tight spaces)
 *   'md'  → 36px button, 20px icon  (default — most modals)
 *   'lg'  → 40px button, 24px icon  (fullscreen / mobile sheets)
 */

interface CloseButtonProps {
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  label?: string;
}

const sizeMap = {
  sm: { button: 32, icon: 'w-5 h-5' },
  md: { button: 40, icon: 'w-6 h-6' },
  lg: { button: 44, icon: 'w-7 h-7' },
};

const CloseButton: React.FC<CloseButtonProps> = ({
  onClick,
  size = 'md',
  className = '',
  label = 'Close',
}) => {
  const { theme } = useTheme();
  const { button: btnSize, icon } = sizeMap[size];

  const baseBg = theme.gray?.[100] || '#F3F4F6';
  const baseColor = theme.gray?.[600] || '#4B5563';
  const hoverBg = theme.gray?.[200] || '#E5E7EB';
  const hoverColor = theme.text || '#1F2937';

  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={`flex items-center justify-center rounded-full transition-colors ${className}`}
      style={{
        width: btnSize,
        height: btnSize,
        backgroundColor: baseBg,
        color: baseColor,
        border: 'none',
        cursor: 'pointer',
        flexShrink: 0,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = hoverBg;
        e.currentTarget.style.color = hoverColor;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = baseBg;
        e.currentTarget.style.color = baseColor;
      }}
    >
      <XMarkIcon className={icon} />
    </button>
  );
};

export default CloseButton;
